import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const LANAPSpecial = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>FREE LANAP Consultation | Laser Gum Treatment | Fort Worth Dental</title>
        <meta name="description" content="FREE LANAP laser gum disease consultation! FDA-cleared alternative to gum surgery. Minimally invasive, faster recovery. Fort Worth TX. Call (817) 985-3337." />
        <link rel="canonical" href="https://fortworthdental.com/specials/lanap" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Free LANAP® Consultation</h1>
          <p className="text-xl">Laser Gum Treatment to Replace Cut and Stitch Gum Surgery</p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          
          {/* Main Content Section */}
          <section className="mb-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Laser Gum Treatment to Replace Cut and Stitch Gum Surgery
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  <strong>LANAP® stands for Laser Assisted New Attachment Procedure.</strong> It's an FDA-cleared 
                  laser treatment for gum disease that provides an alternative to traditional cut-and-stitch gum surgery. 
                  The LANAP protocol uses a special laser to remove diseased tissue and bacteria while leaving healthy gums intact.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Fort Worth Dental is now offering <strong>FREE CONSULTATION for LANAP®</strong> laser gum disease treatment. 
                  Discover if this minimally invasive procedure is right for you.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  <strong>Benefits of LANAP®:</strong>
                </p>
                <ul className="space-y-2 text-gray-700 mb-6">
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">•</span>
                    <span>Less painful than traditional surgery</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">•</span>
                    <span>Faster recovery time</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">•</span>
                    <span>Little to no gum recession</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">•</span>
                    <span>Minimal bleeding and swelling</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">•</span>
                    <span>Can save teeth that might otherwise be lost</span>
                  </li>
                </ul>
              </div>
              <div>
                <img 
                  src="/images/services/lanap-new-1.jpg" 
                  alt="LANAP Laser Gum Treatment" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/laser-new-1.jpg'}
                />
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="mb-12 bg-gradient-to-r from-teal-600 to-teal-700 text-white p-12 rounded-lg text-center">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Free LANAP® Consultation Today</h2>
            <p className="text-xl mb-8">Take the first step toward healthier gums with laser treatment</p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <a 
                href="tel:8178359757" 
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-colors"
              >
                Call (817) 835-9757
              </a>
              <Link 
                to="/services/lanap" 
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700 transition-colors"
              >
                Learn More About LANAP®
              </Link>
            </div>
          </section>

          {/* Testimonial Section */}
          <section className="mb-12 bg-gray-50 p-8 rounded-lg">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">What Others Are Saying</h3>
            <blockquote className="text-gray-700 text-lg italic mb-4">
              "I was terrified of traditional gum surgery, but Dr. Razo recommended LANAP instead. The procedure was 
              so much easier than I expected - minimal pain during and after. My gums have never been healthier and 
              I didn't have to go through the cutting and stitching I was dreading. Highly recommend!"
            </blockquote>
            <p className="text-teal-600 font-semibold">— Jamie L.</p>
          </section>

          {/* What's Included Section */}
          <section className="mb-12">
            <h3 className="text-3xl font-bold text-gray-800 mb-6">Your FREE LANAP® Consultation Includes:</h3>
            <ul className="space-y-4 text-gray-700 text-lg">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-2xl">✓</span>
                <span>Comprehensive periodontal examination</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-2xl">✓</span>
                <span>Assessment of gum disease severity</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-2xl">✓</span>
                <span>Explanation of LANAP® procedure</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-2xl">✓</span>
                <span>Personalized treatment recommendation</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-2xl">✓</span>
                <span>Cost estimate and financing options</span>
              </li>
            </ul>
          </section>

          {/* Appointment Form */}
          <section>
            <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
              Schedule Your FREE LANAP® Consultation
            </h2>
            <p className="text-gray-600 text-center mb-8">
              Fill out the form below or call us at <a href="tel:8178359757" className="text-teal-600 font-semibold">(817) 835-9757</a>
            </p>
            <AppointmentForm />
          </section>

        </div>
      </div>
    </div>
  );
};

export default LANAPSpecial;
